import * as React from 'react';

import { CheckmateDialog } from '../../shared/dialog';
import { DatabaseAppSettingsEnum } from '../../../utilities/Constants';
import { DocumentTitle } from '../../shared/DocumentTitle';
import { GlobalAnnouncements } from '../../shared/announcements/GlobalAnnouncements';
import { HubConnection } from '@microsoft/signalr';
import { IUserModel } from '../../../interfaces/IUser';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { MainContentDivProvider } from '../../../contexts/MainContentDivContext';
import { QuadAdminNavigationMenu } from './QuadAdminNavigationMenu';
import { QuadAdminUserMenu } from './QuadAdminUserMenu';
import { SignalRMethods } from '../../../utilities/SignalR';
import TagManager from 'react-gtm-module';
import { useEffect } from 'react';
import { useGetDbAppSettings } from '../../../shared/react-query-hooks/useGetDbAppSettings';
import { useGetUserUnreadQueryExports } from '../../../shared/react-query-hooks/useGetUserUnreadQueryExports';
import { useNavigate } from 'react-router-dom';

export interface IQuadAdminSecureLayoutProps {
    alertHubConnection: HubConnection | undefined;
    children: React.ReactNode;
    isLoggedIn: boolean;
    user: IUserModel;
}

export function QuadAdminSecureLayout(props: IQuadAdminSecureLayoutProps) {
    const appSettings = useGetDbAppSettings();
    const navigate = useNavigate();
    const mainContainerRef = React.useRef<HTMLDivElement | null>(null);

    const { refetch: refetchReports } = useGetUserUnreadQueryExports(
        props.user ?? { active: false }
    );

    const [showLeavingCenterAdminModal, setShowLeavingCenterAdminModal] =
        React.useState<boolean>(false);

    const handleAppLogoClick = () => {
        setShowLeavingCenterAdminModal(true);
    };

    const handleReturnToZoneClick = () => {
        setShowLeavingCenterAdminModal(true);
    };

    const handleConfirmLeaveCenter = () => {
        navigate(LocalRoutes.QuadDashboard);
    };

    const reportSignalrState = () => {
        console.log(`SignalR - Alert Hub => State: ${props.alertHubConnection?.state}`);
    };

    const startConnection = async () => {
        try {
            if (props.alertHubConnection?.state === 'Disconnected') {
                await props.alertHubConnection?.start();
                reportSignalrState();
            }
        } catch (err) {
            console.log(err);
            reportSignalrState();

            window.setTimeout(() => startConnection(), 10000);
        }
    };

    const bindSignalrEvents = () => {
        const handleSendReportNotificationReceived = () => {
            // console.log(`******* ${SignalRMethods.SendReportNotification} *******`);
            refetchReports();
        };

        if (props.alertHubConnection !== undefined) {
            console.log(
                `SignalR - Alert Hub => Listening for ${SignalRMethods.SendReportNotification}`
            );
            props.alertHubConnection?.on(
                SignalRMethods.SendReportNotification,
                handleSendReportNotificationReceived
            );
            props.alertHubConnection?.onclose(async () => {
                reportSignalrState();
                await startConnection();
            });
        }

        startConnection();
    };

    const unbindSignalrEvents = () => {
        console.log(
            `SignalR - Alert Hub => Stopped listening for ${SignalRMethods.SendReportNotification}`
        );
        props.alertHubConnection?.off(SignalRMethods.SendReportNotification);

        console.log(`SignalR - Alert Hub => Stopped`);
        props.alertHubConnection?.stop();
    };

    useEffect(() => {
        if (!appSettings.isLoading && appSettings.data) {
            const gtmId = appSettings.data.find(
                (setting) => setting.name === DatabaseAppSettingsEnum.GoogleTagManagerId
            )?.value;

            if (gtmId) {
                TagManager.initialize({
                    gtmId,
                });
            } else {
                console.log('Failed to load Google Tag Manager identifier.');
            }
        }
    }, [appSettings.isLoading, appSettings.data]);

    useEffect(() => {
        bindSignalrEvents();
        return unbindSignalrEvents;
    }, [props.alertHubConnection]);

    return (
        <>
            <DocumentTitle hideZoneName />
            <div className="app-container quad-admin-layout">
                <GlobalAnnouncements />
                <CheckmateDialog
                    body="You are leaving the Quad Admin area. Do you wish to continue?"
                    cancelText="Cancel"
                    closeButtonClassName="btn btn-default"
                    confirmButtonClassName="btn btn-orange"
                    confirmText="Leave"
                    handleClose={() => setShowLeavingCenterAdminModal(false)}
                    handleConfirm={handleConfirmLeaveCenter}
                    isShowingModal={showLeavingCenterAdminModal}
                    showBackdrop
                    title="Leave Quad Admin Area"
                />
                <header className="top-nav">
                    <div className="logo-container">
                        <img
                            className="logo-img rounded"
                            src="/images/CheckmateLogo.svg"
                            onClick={handleAppLogoClick}
                            alt="Checkmate Logo"
                        />
                    </div>
                    <div className="nav-menu-item-container">
                        <QuadAdminNavigationMenu />
                        <QuadAdminUserMenu onReturnToZoneClick={handleReturnToZoneClick} />
                    </div>
                </header>
                <div className="app-body">
                    <aside className="left-nav" />
                    <MainContentDivProvider>
                        {props.children}
                    </MainContentDivProvider>
                </div>
            </div>
        </>
    );
}
